import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import AuthButton from '../auth/AuthButton';
import AlertBell from '../alerts/AlertBell';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useCoachView } from '../../contexts/CoachViewContext';
import { APP_VERSION } from '../../version';

const Navigation: React.FC = () => {
  const location = useLocation();
  const { user } = useCurrentUser();
  const { viewAs, viewAsName, isCoachView, relationshipId } = useCoachView();
  const { instance, accounts } = useMsal();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    // Redirect logout — matches AuthButton so both entry points behave identically
    // and Entra External ID properly ends the federated (Google/Microsoft) session.
    instance.logoutRedirect({
      account: accounts[0] ?? null,
      postLogoutRedirectUri: window.location.origin,
    }).catch((e) => {
      console.error('Logout error:', e);
    });
  };

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { path: '/',                  label: 'Dashboard',       icon: '📊' },
    { path: '/metrics',           label: 'Check-Ins',       icon: '📝' },
    { path: '/ped-tracking',      label: 'PED Tracking',    icon: '💉' },
    { path: '/training',          label: 'My Training',     icon: '💪' },
    { path: '/analytics',         label: 'Analytics',       icon: '📈' },
    { path: '/shows',             label: 'Shows',           icon: '🏆' },
    { path: '/seasons',           label: 'Seasons',         icon: '📅' },
    ...(user?.isCoach ? [
      { path: '/coach',           label: 'Coach',           icon: '🏅' },
      { path: '/training/plans',  label: 'Training Plans',  icon: '📋' },
    ] : []),
    { path: '/messages',          label: 'Messages',        icon: '💬' },
    { path: '/settings',          label: 'Settings',        icon: '⚙️' },
  ];

  // When in coach-view mode, inject ?viewAs=...&viewAsName=... into nav links
  // for data pages only. /coach, /messages, /settings and /training/plans always
  // use the coach's own context (no viewAs params).
  const COACH_OWN_ROUTES = ['/coach', '/messages', '/settings', '/training/plans'];
  const buildPath = (path: string): string => {
    if (!isCoachView || COACH_OWN_ROUTES.includes(path)) return path;
    const params = new URLSearchParams();
    params.set('viewAs', viewAs!);
    if (viewAsName)     params.set('viewAsName', viewAsName);
    if (relationshipId) params.set('relationshipId', relationshipId);
    return `${path}?${params.toString()}`;
  };

  return (
    <nav className="bg-dark-800 border-b border-dark-700">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center justify-between h-16">

          {/* ── Logo ── */}
          <div className="flex items-baseline gap-1.5 flex-shrink-0">
            <Link to={buildPath('/')} className="text-2xl font-bold text-gold-500 hover:text-gold-400">
              ShowTracker
            </Link>
            <span className="text-xs text-gray-600 select-none">{APP_VERSION}</span>
          </div>

          {/* ── Desktop nav links ── */}
          <div className="hidden md:flex items-center space-x-4 flex-1 ml-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={buildPath(item.path)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'bg-dark-700 text-gold-500'
                    : 'text-gray-300 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <span className="mr-1">{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </div>

          {/* ── Right side: alert bell + auth button + hamburger ── */}
          <div className="flex items-center gap-3">
            <AlertBell />

            {/* Auth button — desktop only; on mobile it moves into the hamburger menu */}
            <div className="hidden md:flex">
              <AuthButton />
            </div>

            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMobileMenuOpen(o => !o)}
              className="md:hidden flex flex-col justify-center items-center w-10 h-10 rounded-md text-gray-300 hover:bg-dark-700 hover:text-white transition-colors"
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? (
                <span className="text-xl leading-none">✕</span>
              ) : (
                <>
                  <span className="block w-5 h-0.5 bg-current mb-1" />
                  <span className="block w-5 h-0.5 bg-current mb-1" />
                  <span className="block w-5 h-0.5 bg-current" />
                </>
              )}
            </button>
          </div>

        </div>
      </div>

      {/* ── Mobile dropdown menu ── */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-dark-700 bg-dark-800">
          <div className="px-2 py-3 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={buildPath(item.path)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'bg-dark-700 text-gold-500'
                    : 'text-gray-300 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </div>

          {/* Sign-out section at the bottom of the mobile menu */}
          <div className="border-t border-dark-700 px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-gray-400 truncate">
              {accounts[0]?.name || accounts[0]?.username || ''}
            </span>
            <button
              onClick={handleLogout}
              className="ml-3 px-4 py-2 bg-dark-700 text-gray-300 text-sm rounded-lg hover:bg-dark-600 transition-colors flex-shrink-0"
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
